import type { ChatChannelType, TechnicalDataPackage } from '@frontend/api';

import type { ChatId } from 'modules/common/chat/data/types';

import type { ActionsOf } from 'store/types';

import { notify } from 'utils/reduxUtils';

import type {
  ChannelSettingsModalTab,
  SupportingFileAccessInput,
} from './types';

export const types = {
  CHANNEL_SETTINGS_CHANGE_EXPIRATION:
    'chat/shares/CHANNEL_SETTINGS_CHANGE_EXPIRATION',
  CHANNEL_SETTINGS_MODAL_CLOSE: 'chat/shares/CHANNEL_SETTINGS_MODAL_CLOSE',
  CHANNEL_SETTINGS_MODAL_OPEN: 'chat/shares/CHANNEL_SETTINGS_MODAL_OPEN',
  CHANNEL_SETTINGS_MODAL_SET_TAB: 'chat/shares/CHANNEL_SETTINGS_MODAL_SET_TAB',
  CREATE_CHANNEL_ERROR: 'chat/shares/CREATE_CHANNEL_ERROR',
  CREATE_CHANNEL_REQUEST: 'chat/shares/CREATE_CHANNEL_REQUEST',
  CREATE_CHANNEL_SUCCESS: 'chat/shares/CREATE_CHANNEL_SUCCESS',
  CREATE_SHARES_ERROR: 'chat/shares/CREATE_SHARES_ERROR',
  CREATE_SHARES_REQUEST: 'chat/shares/CREATE_SHARES_REQUEST',
  CREATE_SHARES_SUCCESS: 'chat/shares/CREATE_SHARES_SUCCESS',
  DELETE_SHARE_ERROR: 'chat/shares/DELETE_SHARE_ERROR',
  DELETE_SHARE_REQUEST: 'chat/shares/DELETE_SHARE_REQUEST',
  DELETE_SHARE_SUCCESS: 'chat/shares/DELETE_SHARE_SUCCESS',
  DELETE_SHARES_FOR_CHANNEL_ERROR:
    'chat/shares/DELETE_SHARES_FOR_CHANNEL_ERROR',
  DELETE_SHARES_FOR_CHANNEL_REQUEST:
    'chat/shares/DELETE_SHARES_FOR_CHANNEL_REQUEST',
  DELETE_SHARES_FOR_CHANNEL_SUCCESS:
    'chat/shares/DELETE_SHARES_FOR_CHANNEL_SUCCESS',
  INIT_SHARE_SETTINGS_ERROR: 'chat/shares/INIT_SHARE_SETTINGS_ERROR',
  INIT_SHARE_SETTINGS_SUCCESS: 'chat/shares/INIT_SHARE_SETTINGS_SUCCESS',
  NEW_CHANNEL_MODAL_CLOSE: 'chat/shares/NEW_CHANNEL_MODAL_CLOSE',
  NEW_CHANNEL_MODAL_OPEN: 'chat/shares/NEW_CHANNEL_MODAL_OPEN',
  TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_CLOSE:
    'chat/shares/TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_CLOSE',
  TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_OPEN:
    'chat/shares/TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_OPEN',
  UPDATE_CHANNEL_SETTINGS_ERROR: 'chat/shares/UPDATE_CHANNEL_SETTINGS_ERROR',
  UPDATE_CHANNEL_SETTINGS_REQUEST:
    'chat/shares/UPDATE_CHANNEL_SETTINGS_REQUEST',
  UPDATE_CHANNEL_SETTINGS_SUCCESS:
    'chat/shares/UPDATE_CHANNEL_SETTINGS_SUCCESS',
  UPDATE_SHARE_REQUEST: 'chat/shares/UPDATE_SHARE_REQUEST',
  UPDATE_SHARE_SUCCESS: 'chat/shares/UPDATE_SHARE_SUCCESS',
  UPDATE_SHARE_ERROR: 'chat/shares/UPDATE_SHARE_ERROR',
  UPDATE_SHARES_FOR_CHANNEL_ERROR:
    'chat/shares/UPDATE_SHARES_FOR_CHANNEL_ERROR',
  UPDATE_SHARES_FOR_CHANNEL_REQUEST:
    'chat/shares/UPDATE_SHARES_FOR_CHANNEL_REQUEST',
  UPDATE_SHARES_FOR_CHANNEL_SUCCESS:
    'chat/shares/UPDATE_SHARES_FOR_CHANNEL_SUCCESS',
} as const;

export type ChannelIdPayload = {
  channelId: ChatId;
};

export type ChannelSettingsModalOpenPayload = ChannelIdPayload & {
  tab?: ChannelSettingsModalTab;
};

export type ChannelSettingsModalSetTabPayload = {
  tabKey: ChannelSettingsModalTab;
};

export type CreateChannelRequestPayload = {
  accessToAllPartFiles: boolean;
  accessToPrimaryFile: boolean;
  emailSubjectLine: string;
  message: string;
  name: string;
  permissionToDownloadFiles: boolean;
  permissionToUploadFiles: boolean;
  sendInvitesSeparately: boolean;
  sharedWithEmails: string[];
  supportingFilesAccess: SupportingFileAccessInput[];
  type: ChatChannelType;
  /** ISO 8601 */
  expirationDate: string;
};

export type CreateSharesRequestPayload = ChannelIdPayload & {
  emailAddresses: string[];
  emailSubjectLine: string;
  message: string;
  /** ISO 8601 */
  expirationDate: string;
};

export type ShareIdPayload = {
  shareId: ChatId;
};

export type ShareExpirationPayload = ShareIdPayload & {
  /** ISO 8601, or `null` for no expiration */
  expirationDate: string | null;
};

export type UpdateSharesForChannelRequestPayload = ChannelIdPayload & {
  /** ISO 8601 */
  expirationDate: string;
};

export type InitShareSettingsSuccessPayload = {
  technicalDataPackage: TechnicalDataPackage;
};

/** The trusted third party settings form only sends access and permissions */
export type UpdateChannelSettingsRequestPayload = {
  accessToAllPartFiles: boolean;
  accessToPrimaryFile?: boolean;
  name?: string | null;
  supportingFilesAccess: SupportingFileAccessInput[];
  permissionToUploadFiles: boolean;
  permissionToDownloadFiles: boolean;
};

export const channelSettingsModal = {
  close: () => ({
    type: types.CHANNEL_SETTINGS_MODAL_CLOSE,
  }),
  open: ({ channelId, tab }: ChannelSettingsModalOpenPayload) => ({
    type: types.CHANNEL_SETTINGS_MODAL_OPEN,
    payload: { channelId, tab },
  }),
  setTab: ({ tabKey }: ChannelSettingsModalSetTabPayload) => ({
    type: types.CHANNEL_SETTINGS_MODAL_SET_TAB,
    payload: { tabKey },
  }),
};

export const createChannel = {
  error: notify(
    (error: unknown) => ({
      type: types.CREATE_CHANNEL_ERROR,
      payload: { error },
    }),
    'Failed to create channel',
  ),
  request: ({
    accessToAllPartFiles,
    accessToPrimaryFile,
    emailSubjectLine,
    message,
    name,
    permissionToDownloadFiles,
    permissionToUploadFiles,
    sendInvitesSeparately,
    sharedWithEmails,
    supportingFilesAccess,
    type,
    expirationDate,
  }: CreateChannelRequestPayload) => ({
    type: types.CREATE_CHANNEL_REQUEST,
    payload: {
      accessToAllPartFiles,
      accessToPrimaryFile,
      emailSubjectLine,
      message,
      name,
      permissionToDownloadFiles,
      permissionToUploadFiles,
      sendInvitesSeparately,
      sharedWithEmails,
      supportingFilesAccess,
      type,
      expirationDate,
    },
  }),
  success: () => ({
    type: types.CREATE_CHANNEL_SUCCESS,
  }),
};

export const createShares = {
  error: notify(
    (error: unknown) => ({
      type: types.CREATE_SHARES_ERROR,
      payload: error,
    }),
    'Failed to share channel',
  ),
  request: ({
    channelId,
    emailAddresses,
    emailSubjectLine,
    message,
    expirationDate,
  }: CreateSharesRequestPayload) => ({
    type: types.CREATE_SHARES_REQUEST,
    payload: {
      channelId,
      emailAddresses,
      emailSubjectLine,
      message,
      expirationDate,
    },
  }),
  success: notify(
    () => ({
      type: types.CREATE_SHARES_SUCCESS,
    }),
    'Share invites sent',
  ),
};

export const deleteShare = {
  error: notify(
    (error: unknown) => ({
      type: types.DELETE_SHARE_ERROR,
      payload: error,
    }),
    'Failed to remove user from channel',
  ),
  request: ({ shareId }: ShareIdPayload) => ({
    type: types.DELETE_SHARE_REQUEST,
    payload: { shareId },
  }),
  success: notify(
    () => ({
      type: types.DELETE_SHARE_SUCCESS,
    }),
    'User removed from channel',
  ),
};

export const updateShare = {
  error: notify(
    (error: unknown) => ({
      type: types.UPDATE_SHARE_ERROR,
      payload: error,
    }),
    'Failed to update share',
  ),
  request: ({ shareId, expirationDate }: ShareExpirationPayload) => ({
    type: types.UPDATE_SHARE_REQUEST,
    payload: { shareId, expirationDate },
  }),
  success: () => ({
    type: types.UPDATE_SHARE_SUCCESS,
  }),
};

export const updateSharesForChannel = {
  error: notify(
    (error: unknown) => ({
      type: types.UPDATE_SHARES_FOR_CHANNEL_ERROR,
      payload: error,
    }),
    'Failed to update shares',
  ),
  request: ({
    channelId,
    expirationDate,
  }: UpdateSharesForChannelRequestPayload) => ({
    type: types.UPDATE_SHARES_FOR_CHANNEL_REQUEST,
    payload: { channelId, expirationDate },
  }),
  success: () => ({
    type: types.UPDATE_SHARES_FOR_CHANNEL_SUCCESS,
  }),
};

export const deleteSharesForChannel = {
  error: notify(
    (error: unknown) => ({
      type: types.DELETE_SHARES_FOR_CHANNEL_ERROR,
      payload: error,
    }),
    'Failed to revoke access to channel',
  ),
  request: ({ channelId }: ChannelIdPayload) => ({
    type: types.DELETE_SHARES_FOR_CHANNEL_REQUEST,
    payload: { channelId },
  }),
  success: notify(
    () => ({
      type: types.DELETE_SHARES_FOR_CHANNEL_SUCCESS,
    }),
    'Channel access revoked',
  ),
};

export const initShareSettings = {
  error: notify(
    (error: unknown) => ({
      type: types.INIT_SHARE_SETTINGS_ERROR,
      payload: error,
    }),
    'Failed to inititate share settings',
  ),
  success: ({ technicalDataPackage }: InitShareSettingsSuccessPayload) => ({
    type: types.INIT_SHARE_SETTINGS_SUCCESS,
    payload: { technicalDataPackage },
  }),
};

export const trustedThirdPartyChannelSettingsModal = {
  close: () => ({
    type: types.TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_CLOSE,
  }),
  open: ({ channelId }: ChannelIdPayload) => ({
    type: types.TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_OPEN,
    payload: { channelId },
  }),
};

export const newChannelModal = {
  close: () => ({
    type: types.NEW_CHANNEL_MODAL_CLOSE,
  }),
  open: () => ({
    type: types.NEW_CHANNEL_MODAL_OPEN,
  }),
};

export const updateChannelSettings = {
  error: notify(
    (error: unknown) => ({
      type: types.UPDATE_CHANNEL_SETTINGS_ERROR,
      payload: error,
    }),
    'Failed to update channel settings',
  ),
  request: ({
    accessToAllPartFiles,
    accessToPrimaryFile,
    name,
    supportingFilesAccess,
    permissionToUploadFiles,
    permissionToDownloadFiles,
  }: UpdateChannelSettingsRequestPayload) => ({
    type: types.UPDATE_CHANNEL_SETTINGS_REQUEST,
    payload: {
      accessToAllPartFiles,
      accessToPrimaryFile,
      name,
      supportingFilesAccess,
      permissionToUploadFiles,
      permissionToDownloadFiles,
    },
  }),
  success: notify(
    () => ({
      type: types.UPDATE_CHANNEL_SETTINGS_SUCCESS,
    }),
    'Channel settings saved',
  ),
};

export const changeExpirationDate = ({
  shareId,
  expirationDate,
}: ShareExpirationPayload) => ({
  type: types.CHANNEL_SETTINGS_CHANGE_EXPIRATION,
  payload: { shareId, expirationDate },
});

export type ChatSharesAction = ActionsOf<{
  changeExpirationDate: typeof changeExpirationDate;
  channelSettingsModal: typeof channelSettingsModal;
  createChannel: typeof createChannel;
  createShares: typeof createShares;
  deleteShare: typeof deleteShare;
  deleteSharesForChannel: typeof deleteSharesForChannel;
  initShareSettings: typeof initShareSettings;
  newChannelModal: typeof newChannelModal;
  trustedThirdPartyChannelSettingsModal: typeof trustedThirdPartyChannelSettingsModal;
  updateChannelSettings: typeof updateChannelSettings;
  updateShare: typeof updateShare;
  updateSharesForChannel: typeof updateSharesForChannel;
}>;
